/**
 * TicketTable Component
 *
 * Displays a table of support tickets with status, priority, and actions.
 */

import React from 'react';
import {
  TicketIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketPriorityBadge } from './TicketPriorityBadge';
import type { SupportTicketSanitized } from '../../../types/support-ticket.types';
import { categoryLabels } from '../../../types/support-ticket.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface TicketTableProps {
  tickets: SupportTicketSanitized[];
  isLoading?: boolean;
  onViewTicket: (ticket: SupportTicketSanitized) => void;
}

/**
 * Check if SLA is breached or at risk
 */
const getSlaStatus = (ticket: SupportTicketSanitized): 'breached' | 'warning' | 'ok' => {
  if (ticket.slaBreach) return 'breached';
  if (ticket.firstResponseAt) return 'ok';

  const slaDeadline = new Date(ticket.slaDeadline);
  const now = new Date();
  const hoursRemaining = (slaDeadline.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursRemaining < 0) return 'breached';
  if (hoursRemaining < 8) return 'warning'; // Less than 8 hours remaining
  return 'ok';
};

/**
 * TicketTable component
 */
export const TicketTable: React.FC<TicketTableProps> = ({
  tickets,
  isLoading,
  onViewTicket,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (tickets.length === 0) {
    return (
      <EmptyState
        icon={<TicketIcon className="mx-auto h-12 w-12" />}
        title="No support tickets"
        description="Create a new ticket to get help from our support team."
      />
    );
  }

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Ticket
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Priority
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Category
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Last Update
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {tickets.map((ticket) => {
            const slaStatus = getSlaStatus(ticket);

            return (
              <tr
                key={ticket.ticketId}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onViewTicket(ticket)}
              >
                <td className="py-4 pl-4 pr-3 sm:pl-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <TicketIcon className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {ticket.subject}
                        </span>
                        {slaStatus === 'breached' && (
                          <ExclamationTriangleIcon className="h-4 w-4 text-red-500" title="SLA breached" />
                        )}
                        {slaStatus === 'warning' && (
                          <ClockIcon className="h-4 w-4 text-orange-500" title="SLA at risk" />
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                        <span className="font-mono">{ticket.referenceNumber}</span>
                        <span>&middot;</span>
                        <span>by {ticket.createdByName}</span>
                        <span>&middot;</span>
                        <span className="flex items-center gap-1">
                          <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                          {ticket.messageCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-4">
                  <TicketStatusBadge status={ticket.status} />
                </td>
                <td className="whitespace-nowrap px-3 py-4">
                  <TicketPriorityBadge priority={ticket.priority} />
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {categoryLabels[ticket.category]}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {ticket.lastMessageAt
                    ? formatRelativeTime(ticket.lastMessageAt)
                    : formatRelativeTime(ticket.createdAt)}
                </td>
                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                  <button
                    className="text-blue-600 hover:text-blue-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewTicket(ticket);
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
